/**
 * Turn an ordered list of clicked wall points into run segments.
 * Pure. Each click = { wall, point } where `wall` carries
 * `alignment.bottomLeft/bottomRight` and `point` is the world {x,z} on it.
 *
 * Consecutive clicks on the SAME wall form one segment. When two consecutive
 * clicks are on DIFFERENT walls, we infer the corner as the intersection of
 * the two wall lines and split into two segments that share that exact corner
 * vertex — so the engine's corner detector (which needs the shared vertex to
 * coincide within 1 mm) fires reliably even though the user's click never
 * lands exactly on the geometric corner.
 *
 * See claude-docs/2026-06-13-manual-cabinet-runs-plan-05-draw-tool.md.
 */

const {
  worldToWallParam,
  getWallAxes,
} = require('../kitchen-assembly/wallMath');
const {
  RUN_ENDPOINT_SNAP_TOLERANCE,
} = require('../kitchen-assembly/constants');
const { pointSegmentDistance } = require('../geometry/segmentGeometry');
const { JOIN_EPS, isStraightOnTurn, turnDeg } = require('./bendGeometry');
const { placeFreeLegsAlongWalls } = require('./freeLegsAlongWalls');

/**
 * How near a wall the draw tool reaches: a click this close to a wall lands
 * on it (the web's `SNAP_TOLERANCE` is this), a corner two walls make counts
 * only this close to both, and a drawn wall leg may run no further than this
 * past its wall (`wallLegsPastTheirWalls`). The user, 2026-11-05: "0.8m is
 * fine".
 */
const WALL_SNAP_REACH = 0.8;

const ZERO_LEN_EPS = 1e-4; // meters — drop slivers (e.g. a click already on the corner)
// Free-standing legs shorter than this are click artifacts — most commonly the
// duplicate end point a finishing double-click tacks on (a ~1 cm perpendicular
// stub). No real island/peninsula leg is shorter than a single narrow cabinet.
const MIN_FREE_SEGMENT_LEN = 0.05; // meters (~2")

/** Intersection of two walls' infinite centerlines, or null if parallel. */
function wallLineIntersection(wallA, wallB) {
  const a = wallA.alignment?.bottomLeft;
  const b = wallA.alignment?.bottomRight;
  const c = wallB.alignment?.bottomLeft;
  const d = wallB.alignment?.bottomRight;
  if (!a || !b || !c || !d) return null;
  const r = { x: b.x - a.x, z: b.z - a.z };
  const s = { x: d.x - c.x, z: d.z - c.z };
  const denom = r.x * s.z - r.z * s.x;
  if (Math.abs(denom) < 1e-9) return null; // parallel / collinear
  const t = ((c.x - a.x) * s.z - (c.z - a.z) * s.x) / denom;
  return { x: a.x + t * r.x, z: a.z + t * r.z };
}

/** A wall's two ends, as points on its line. */
function wallEnds(wall) {
  const a = wall?.alignment?.bottomLeft;
  const b = wall?.alignment?.bottomRight;
  return a && b ? [a, b] : null;
}

const distToWall = (p, wall) => {
  const ends = wallEnds(wall);
  return ends ? pointSegmentDistance(p, ends[0], ends[1]) : Infinity;
};

/**
 * Where two walls meet, if they do: their lines' crossing, counted only
 * within `WALL_SNAP_REACH` of both walls. Two walls a fraction of a degree
 * off parallel "cross" kilometres away — on Robinson/Mckinzie Scan 1
 * (2026-11-05) about 21 km, a run of two 21 km legs that took the browser
 * down filling it.
 */
function cornerOf(wallA, wallB) {
  const corner = wallLineIntersection(wallA, wallB);
  if (!corner) return null;
  if (distToWall(corner, wallA) > WALL_SNAP_REACH) return null;
  if (distToWall(corner, wallB) > WALL_SNAP_REACH) return null;
  return corner;
}

/** Does `point` lie on `wall`'s own line (within the run's end-snap)? */
function onWallLine(point, wall) {
  const ax = getWallAxes(wall);
  if (!ax) return false;
  const off =
    (point.x - ax.start.x) * ax.wallDir.z -
    (point.z - ax.start.z) * ax.wallDir.x;
  return Math.abs(off) <= RUN_ENDPOINT_SNAP_TOLERANCE;
}

/**
 * A third wall both clicks lie on, that meets each of theirs: the wall a leg
 * between two corners runs along — the bottom wall between a click at its
 * left corner (snapped to the left wall) and one at its right corner
 * (snapped to the right).
 */
function wallBetween(a, b, walls) {
  for (const w of walls || []) {
    if (w.id === a.wall.id || w.id === b.wall.id) continue;
    if (distToWall(a.point, w) > RUN_ENDPOINT_SNAP_TOLERANCE) continue;
    if (distToWall(b.point, w) > RUN_ENDPOINT_SNAP_TOLERANCE) continue;
    const ca = cornerOf(a.wall, w);
    const cb = cornerOf(w, b.wall);
    if (ca && cb) return { wall: w, ca, cb };
  }
  return null;
}

function seg(wall, fromPoint, toPoint) {
  return {
    wall,
    startParam: worldToWallParam(wall, fromPoint),
    endParam: worldToWallParam(wall, toPoint),
  };
}

/**
 * Two consecutive free legs that meet and turn less than the draw tool's snap
 * catch (`BEND_SNAP_DEG`). The snap straightens any such turn, so they are one
 * leg drawn with an extra click — and a bend of 0° is no bend to fill.
 */
function freeLegsRunStraightOn(a, b) {
  if (!a?.freestanding || !b?.freestanding) return false;
  if (Math.hypot(a.end.x - b.start.x, a.end.z - b.start.z) > JOIN_EPS) {
    return false;
  }
  const dir = (s) => {
    const len = Math.hypot(s.end.x - s.start.x, s.end.z - s.start.z);
    return len > 1e-9
      ? { x: (s.end.x - s.start.x) / len, z: (s.end.z - s.start.z) / len }
      : null;
  };
  const dA = dir(a);
  const dB = dir(b);
  if (!dA || !dB) return false;
  return isStraightOnTurn(turnDeg(dA, dB));
}

/**
 * @param {Array<{wall: Object|null, point: {x,z}}>} clicks
 * @param {{ walls?: Object[] }} [opts]  `walls`: the room's walls, resolved
 *   the way the clicks' are. Given, a free leg that lies along one of them is
 *   moved onto it (`freeLegsAlongWalls.js`): the drawn path's legs, as walls
 *   and free-standing pieces past their ends.
 */
function buildRunSegments(clicks, { walls = null } = {}) {
  if (!Array.isArray(clicks) || clicks.length < 2) return [];

  const raw = [];
  for (let i = 0; i < clicks.length - 1; i += 1) {
    const a = clicks[i];
    const b = clicks[i + 1];

    if (!a.wall || !b.wall) {
      // Free-standing leg: the backing line is the two click points themselves.
      raw.push({ freestanding: true, start: a.point, end: b.point });
      continue;
    }

    if (a.wall.id === b.wall.id) {
      raw.push(seg(a.wall, a.point, b.point));
      continue;
    }

    // Wall change: infer the shared corner and split into two legs.
    const corner = cornerOf(a.wall, b.wall);
    if (corner) {
      raw.push(seg(a.wall, a.point, corner));
      raw.push(seg(b.wall, corner, b.point));
      continue;
    }
    // Two pieces of one wall (parallel, on one line): a single span on the
    // second.
    if (!wallLineIntersection(a.wall, b.wall) && onWallLine(a.point, b.wall)) {
      raw.push(seg(b.wall, a.point, b.point));
      continue;
    }
    // Walls that do not meet near the clicks: the leg runs along the wall both
    // clicks lie on, or — with none — straight between them, free-standing.
    const via = wallBetween(a, b, walls);
    if (via) {
      raw.push(seg(a.wall, a.point, via.ca));
      raw.push(seg(via.wall, via.ca, via.cb));
      raw.push(seg(b.wall, via.cb, b.point));
    } else {
      raw.push({ freestanding: true, start: a.point, end: b.point });
    }
  }

  // Merge consecutive segments on the same wall. A middle click that lands
  // near a corner produces a tiny sliver leg + the real run on the same wall;
  // keeping them separate makes the corner reservation land on the sliver
  // (which then gets skipped as too short), leaving the real run unreserved
  // and its cabinets overrunning into the corner cabinet. Merging keeps the
  // run as one segment so the corner reservation lands on it.
  const placed = placeFreeLegsAlongWalls(raw, walls);
  const merged = [];
  for (const s of placed) {
    const last = merged[merged.length - 1];
    if (last && last.wall && s.wall && last.wall.id === s.wall.id) {
      last.endParam = s.endParam; // contiguous same-wall run → extend
    } else if (freeLegsRunStraightOn(last, s)) {
      last.end = s.end; // the snap made them one straight leg → one leg
    } else {
      merged.push({ ...s });
    }
  }

  // Drop near-zero-length slivers: wall legs that collapse onto a corner, and
  // free-standing stubs from a duplicate finishing click (see MIN_FREE_SEGMENT_LEN).
  return merged.filter((s) =>
    s.freestanding
      ? Math.hypot(s.end.x - s.start.x, s.end.z - s.start.z) >
        MIN_FREE_SEGMENT_LEN
      : Math.abs(s.endParam - s.startParam) > ZERO_LEN_EPS
  );
}

/**
 * The wall legs that run more than `reach` past either end of their own wall
 * — never a run the draw tool should build. The backstop behind `cornerOf`:
 * the click and the finish refuse a run that has one, rather than fill it.
 *
 * @returns {number[]} the legs' indices
 */
function wallLegsPastTheirWalls(segments, reach = WALL_SNAP_REACH) {
  const out = [];
  (segments || []).forEach((s, i) => {
    if (s?.freestanding || !s?.wall) return;
    const len = getWallAxes(s.wall)?.wallLen;
    if (!Number.isFinite(len)) return;
    const lo = Math.min(s.startParam, s.endParam);
    const hi = Math.max(s.startParam, s.endParam);
    if (lo < -reach || hi > len + reach) out.push(i);
  });
  return out;
}

module.exports = {
  buildRunSegments,
  wallLineIntersection,
  wallLegsPastTheirWalls,
  MIN_FREE_SEGMENT_LEN,
  WALL_SNAP_REACH,
};
